/**
 * Owner: Masa Bayu
 * File: text-language.ts
 * Date:Time: 2026-09-20 21:26
 * Description: CA-07 / Phase 1E language identity · inheritance · RTL/typography binding.
 */
import { adviseBidiIsolation, normalizeLanguageId, resolveDirectionHint, resolveTypographyForScript, } from '@qx/qx-language';
/**
 * v1 Supported language primaries (Batch 1–7 surface).
 * Registry shape ≠ Supported — unknown or out-of-v1 → reject.
 */
export const QX_TEXT_V1_SUPPORTED_LANGUAGE_PRIMARIES = [
    'en',
    'ms',
    'id',
    'ar',
    'fa',
    'ur',
    'he',
    'yi',
    'zh',
    'ja',
    'ko',
    'ru',
    'uk',
    'bg',
    'sr',
    'el',
    'hy',
    'ka',
    'th',
    'lo',
    'km',
    'my',
    'hi',
    'bn',
    'ta',
    'te',
    'ml',
    'gu',
    'pa',
    'kn',
    'or',
    'as',
    'mr',
    'ne',
    'si',
    'am',
    'ti',
    'bo',
    'fr',
    'de',
    'es',
    'pt',
    'it',
    'nl',
    'pl',
    'tr',
    'vi',
    'tl',
];
const SUPPORTED_PRIMARY = new Set(QX_TEXT_V1_SUPPORTED_LANGUAGE_PRIMARIES);
export function createQXTextLanguage(languageCode) {
    const canonical = assertQXTextLanguageCode(languageCode);
    return Object.freeze({ languageCode: canonical });
}
/**
 * Normalize + fail-closed for unknown shape or unsupported-for-v1 primary.
 * No silent substitution.
 */
export function assertQXTextLanguageCode(raw) {
    let parsed;
    try {
        parsed = normalizeLanguageId(raw);
    }
    catch {
        throw new Error(`unknown or invalid languageCode: ${raw}`);
    }
    if (!SUPPORTED_PRIMARY.has(parsed.primary)) {
        throw new Error(`unsupported languageCode for v1: ${parsed.languageId}`);
    }
    return parsed.languageId;
}
export function isQXTextLanguageCodeSupported(raw) {
    try {
        assertQXTextLanguageCode(raw);
        return true;
    }
    catch {
        return false;
    }
}
/**
 * Direction derived from resolved language — presentation consequence only.
 * `auto` is rejected, never returned: an ambiguous hint is a hard failure (B5),
 * so callers that publish a direction may rely on `ltr | rtl`.
 */
export function deriveQXTextDirection(languageCode) {
    const canonical = assertQXTextLanguageCode(languageCode);
    const hint = resolveDirectionHint(canonical);
    if (hint.hintedDirection === 'auto') {
        throw new Error(`ambiguous language-direction state for: ${canonical}`);
    }
    return hint.hintedDirection;
}
/**
 * Typography binding via QXTypographySystem mapping (qx-language GL-06).
 * Does not write font/CSS fields into ContentNode.
 */
export function bindQXTextTypography(languageCode, role = 'body') {
    const canonical = assertQXTextLanguageCode(languageCode);
    return resolveTypographyForScript({ languageId: canonical, role });
}
/** Presentation-layer bidi isolation advice — not stored in QXW. */
export function adviseQXTextBidiIsolation(baseDirection, embeddedDirection) {
    return adviseBidiIsolation(baseDirection, embeddedDirection);
}
function languageOf(node) {
    return node?.language?.languageCode ?? null;
}
/**
 * Deterministic inheritance (CA-07 L2):
 * explicit → parent → content → document → unresolved
 */
export function resolveQXTextLanguage(input) {
    if (input.explicit?.languageCode) {
        const languageCode = assertQXTextLanguageCode(input.explicit.languageCode);
        return {
            languageCode,
            source: 'explicit',
            direction: deriveQXTextDirection(languageCode),
        };
    }
    if (input.parentLanguageCode) {
        const languageCode = assertQXTextLanguageCode(input.parentLanguageCode);
        return {
            languageCode,
            source: 'parent',
            direction: deriveQXTextDirection(languageCode),
        };
    }
    if (input.contentLanguageCode) {
        const languageCode = assertQXTextLanguageCode(input.contentLanguageCode);
        return {
            languageCode,
            source: 'content',
            direction: deriveQXTextDirection(languageCode),
        };
    }
    if (input.documentLanguageCode) {
        const languageCode = assertQXTextLanguageCode(input.documentLanguageCode);
        return {
            languageCode,
            source: 'document',
            direction: deriveQXTextDirection(languageCode),
        };
    }
    return { languageCode: null, source: 'unresolved', direction: null };
}
/** Resolve language for a top-level body block. */
export function resolveQXTextBlockLanguage(content, blockIndex, documentLanguageCode) {
    const block = content.blocks[blockIndex];
    if (block === undefined) {
        throw new Error('block not found');
    }
    return resolveQXTextLanguage({
        explicit: block.type === 'rule' ? null : (block.language ?? null),
        contentLanguageCode: languageOf(content),
        documentLanguageCode: documentLanguageCode ?? null,
    });
}
/** Resolve caption language under a figure (parent = figure resolved language). */
export function resolveQXTextCaptionLanguage(figure, content, documentLanguageCode) {
    const parent = resolveQXTextLanguage({
        explicit: figure.language ?? null,
        contentLanguageCode: languageOf(content),
        documentLanguageCode: documentLanguageCode ?? null,
    });
    const caption = figure.caption;
    return resolveQXTextLanguage({
        explicit: caption?.language ?? null,
        parentLanguageCode: parent.languageCode,
        contentLanguageCode: languageOf(content),
        documentLanguageCode: documentLanguageCode ?? null,
    });
}
function replaceBlockLanguage(block, language) {
    if (block.type === 'rule')
        return block;
    if (language === undefined) {
        if (block.language === undefined) {
            return block;
        }
        const { language: _removed, ...rest } = block;
        void _removed;
        return rest;
    }
    return { ...block, language };
}
export function setQXTextBlockLanguage(model, blockIndex, languageCode) {
    const block = model.blocks[blockIndex];
    if (block === undefined) {
        throw new Error('block not found');
    }
    const language = createQXTextLanguage(languageCode);
    const blocks = model.blocks.slice();
    blocks[blockIndex] = replaceBlockLanguage(block, language);
    return { blocks, ...(model.language !== undefined ? { language: model.language } : {}) };
}
export function clearQXTextBlockLanguage(model, blockIndex) {
    const block = model.blocks[blockIndex];
    if (block === undefined) {
        throw new Error('block not found');
    }
    const blocks = model.blocks.slice();
    blocks[blockIndex] = replaceBlockLanguage(block, undefined);
    return { blocks, ...(model.language !== undefined ? { language: model.language } : {}) };
}
export function setQXTextContentLanguage(content, languageCode) {
    return {
        blocks: content.blocks,
        language: createQXTextLanguage(languageCode),
    };
}
export function clearQXTextContentLanguage(content) {
    return { blocks: content.blocks };
}
/** Preserve logical text order — identity join; no visual rewrite. */
export function readQXTextLogicalPlainText(content) {
    const fromInlines = (inlines) => inlines
        .map((inline) => (inline.type === 'note-ref' ? '' : (inline.text ?? '')))
        .join('');
    return content.blocks
        .map((block) => {
        if (block.type === 'list') {
            return block.items.map((item) => fromInlines(item.inlines)).join('\n');
        }
        if (block.type === 'figure') {
            return block.caption === undefined ? '' : fromInlines(block.caption.inlines);
        }
        if (block.type === 'rule')
            return '';
        return fromInlines(block.inlines);
    })
        .join('\n');
}
//# sourceMappingURL=text-language.js.map